/**
 * Wie eine Textzeile im Bild gesetzt wird: Umbruch, Schriftgröße, Balken.
 *
 * Diese Datei ist die EINZIGE Quelle dafür, und zwar für beide Seiten:
 * Der FFmpeg-Renderer brennt danach ein, die Vorschau zeichnet danach.
 * Vorher rechnete jede Seite selbst — der Renderer mit einer Faustformel,
 * die Vorschau mit CSS-Umbruch. Zwei Verfahren, zwei Ergebnisse: Was in
 * der Vorschau dreizeilig stand, kam als sechs Zeilen aus dem Video.
 *
 * Die Regeln stammen von dem, was TikTok und Instagram selbst tun:
 *
 *   LIEBER KLEINER ALS ZERRISSEN. Ein langer Satz wird nicht in
 *   Einzelwörter zerhackt, sondern die Schrift wird kleiner, bis er in
 *   wenige volle Zeilen passt. „Vier / Symbole / aus einer / Tasse."
 *   liest niemand als Satz.
 *
 *   GLEICHMÄSSIGE ZEILEN. Umgebrochen wird ausgewogen, nicht gierig: vier
 *   Zeilen zu je drei Wörtern statt drei volle und eine mit einem Wort.
 *
 *   KEINE WAISEN. Ein einzelnes kurzes Wort auf der letzten Zeile wird
 *   nach oben gezogen.
 */
import { RENDER_PRESET } from './platform.js';
/*
 * Bewusst als `number` und nicht als Literaltyp: RENDER_PRESET ist `as const`,
 * sonst erbten alle Vorgabewerte hier den Typ `1080` und jede Vorschau mit
 * echter Pixelbreite waere ein Typfehler.
 */
const CANVAS_W = RENDER_PRESET.width;
const CANVAS_H = RENDER_PRESET.height;
/** Schriftgrößen in Pixeln, bezogen auf die Canvas-Breite von 1080. */
export const TEXT_SIZE_PX = { S: 44, M: 60, L: 82, XL: 108 };
/**
 * Anteil der Breite, den ein Textblock höchstens einnimmt.
 *
 * Nicht Geschmack, sondern die Buttonleiste: TikTok legt rechts eine rund
 * 120 bis 140 px breite Spalte mit Like, Kommentar und Teilen über das
 * Video, Instagram ebenso etwa 120 px. Ein zentrierter Block darf deshalb
 * höchstens bis etwa x=950 reichen -- 76 % Breite lassen beidseitig 130 px
 * frei. Die alten 84 % liefen rechts sichtbar unter die Icons.
 */
export const TEXT_MAX_WIDTH = 0.76;
/** Zeilenabstand als Vielfaches der Schriftgröße. */
export const TEXT_LINE_HEIGHT = 1.2;
/**
 * Der senkrechte Bereich, den die Plattformen nicht mit eigener Bedienung
 * überdecken, als Anteil von 1920 px Höhe.
 *
 * Die Zahlen stammen aus den Safe-Zone-Angaben beider Plattformen (Stand
 * 2026): oben belegen Name, Suchleiste und Follow-Knopf bis etwa 250 px
 * (Instagram ist dort strenger als TikTok mit ~160 px), unten Caption,
 * Musikzeile und CTA bis etwa 480 px ab Unterkante -- Text muss also
 * oberhalb von y≈1436 enden. 0,70 lässt dazu noch gut 90 px Luft, denn die
 * Caption-Höhe wächst mit ihrer Länge und die Grenze ist keine feste.
 */
export const TEXT_SAFE_TOP = 0.13;
export const TEXT_SAFE_BOTTOM = 0.70;
/** Höchstens so viele Zeilen, darüber wird die Schrift kleiner. */
const MAX_LINES = 4;
/** Unter diese Größe wird nicht verkleinert, sonst liest es niemand mehr. */
const MIN_FONT_PX = 34;
/**
 * Wie breit ein Zeichen in einer fetten Grotesk ungefähr ist, als Vielfaches
 * der Schriftgröße.
 *
 * Ein Pauschalfaktor reicht hier nicht: „Illiiii" und „Wammmw" sind bei
 * gleicher Zeichenzahl fast doppelt so verschieden breit. Ohne diese
 * Unterscheidung bricht der Renderer mal zu früh und mal zu spät um, und die
 * Vorschau zeigt wieder etwas anderes. Die Werte sind an Inter/Manrope Bold
 * gemessen und für beide Seiten identisch.
 */
const NARROW = new Set('iíìîïjlItfríĩ.,;:!|\'"`()[]{}/\\ ');
const WIDE = new Set('mwMWQ@%&—–');
function charWidth(ch) {
    if (NARROW.has(ch))
        return ch === ' ' ? 0.28 : 0.32;
    if (WIDE.has(ch))
        return 0.88;
    if (ch >= 'A' && ch <= 'Z')
        return 0.68;
    if (ch >= '0' && ch <= '9')
        return 0.58;
    return 0.56;
}
/** Breite eines Wortes oder einer Zeile in Pixeln, bei gegebener Schriftgröße. */
export function measureText(text, fontPx) {
    let sum = 0;
    for (const ch of text)
        sum += charWidth(ch);
    return sum * fontPx;
}
/**
 * Bricht einen Text auf höchstens `maxLines` möglichst gleichmäßige Zeilen um.
 *
 * Gibt null zurück, wenn der Text bei dieser Schriftgröße nicht hineinpasst.
 * Der Aufrufer verkleinert dann und fragt erneut.
 */
function wrapBalanced(text, fontPx, maxWidth, maxLines) {
    const words = text.split(/\s+/).filter(Boolean);
    if (!words.length)
        return [];
    // Ein einzelnes Wort, das schon allein zu breit ist, passt bei keiner
    // Zeilenaufteilung. Dann muss die Schrift kleiner werden.
    for (const w of words) {
        if (measureText(w, fontPx) > maxWidth)
            return null;
    }
    /*
     * Erst die kleinstmögliche Zeilenzahl bestimmen (gieriger Umbruch), dann
     * die Wörter auf genau diese Zeilen ausgewogen verteilen.
     */
    const greedy = wrapGreedy(words, fontPx, maxWidth);
    if (greedy.length > maxLines)
        return null;
    return wrapBalancedInto(words, fontPx, maxWidth, greedy.length) ?? greedy;
}
function wrapGreedy(words, fontPx, maxWidth) {
    const lines = [];
    let cur = '';
    for (const w of words) {
        const next = cur ? `${cur} ${w}` : w;
        if (!cur || measureText(next, fontPx) <= maxWidth)
            cur = next;
        else {
            lines.push(cur);
            cur = w;
        }
    }
    if (cur)
        lines.push(cur);
    return lines;
}
/**
 * Verteilt die Wörter auf genau `n` Zeilen, so gleichmäßig wie möglich.
 *
 * Das ist der Kern gegen „ein Wort pro Zeile". Gieriger Umbruch füllt Zeile
 * für Zeile bis zum Anschlag und lässt übrig, was übrig bleibt -- so entsteht
 * „Die Vokale in / deinem / Namen sagen, / was du willst": drei volle Zeilen
 * und ein einzelnes Wort dazwischen, weil „deinem Namen" gerade nicht mehr
 * passte.
 *
 * Stattdessen wird hier über alle möglichen Aufteilungen gerechnet und die
 * gewählt, bei der die Zeilen am ähnlichsten breit sind (Summe der
 * quadrierten Restbreiten, das klassische Blocksatz-Maß). Eine sehr kurze
 * Zeile ist darin teuer, egal an welcher Stelle sie steht -- Waisen fallen
 * damit von selbst weg, ohne Sonderregel.
 *
 * Der Aufwand ist Wörter × Wörter × Zeilen, bei höchstens ein paar Dutzend
 * Wörtern und vier Zeilen also nicht der Rede wert.
 */
function wrapBalancedInto(words, fontPx, maxWidth, n) {
    const count = words.length;
    if (n <= 1 || count <= n) {
        return count <= n ? words.slice() : null;
    }
    // width[i][j] = Breite der Zeile aus words[i..j], Infinity wenn zu breit.
    const width = Array.from({ length: count }, () => new Array(count).fill(Infinity));
    for (let i = 0; i < count; i++) {
        let text = '';
        for (let j = i; j < count; j++) {
            text = text ? `${text} ${words[j]}` : words[j];
            const w = measureText(text, fontPx);
            width[i][j] = w <= maxWidth ? w : Infinity;
        }
    }
    // best[k][i] = geringste Kosten, words[i..] in genau k Zeilen zu setzen.
    const best = Array.from({ length: n + 1 }, () => new Array(count + 1).fill(Infinity));
    const cut = Array.from({ length: n + 1 }, () => new Array(count + 1).fill(-1));
    best[0][count] = 0;
    for (let k = 1; k <= n; k++) {
        for (let i = count - 1; i >= 0; i--) {
            for (let j = i; j < count; j++) {
                if (width[i][j] === Infinity)
                    break;
                const rest = best[k - 1][j + 1];
                if (rest === Infinity)
                    continue;
                const slack = maxWidth - width[i][j];
                const cost = slack * slack + rest;
                if (cost < best[k][i]) {
                    best[k][i] = cost;
                    cut[k][i] = j;
                }
            }
        }
    }
    if (best[n][0] === Infinity)
        return null;
    const lines = [];
    let i = 0;
    for (let k = n; k >= 1; k--) {
        const j = cut[k][i];
        if (j < 0)
            return null;
        lines.push(words.slice(i, j + 1).join(' '));
        i = j + 1;
    }
    return lines;
}
/**
 * Der fertige Satz einer Zeile: Umbruch und Schriftgröße in einem.
 *
 * `size` ist der Wunsch des Planers, nicht das letzte Wort. Ein voller Satz
 * in XL wird so lange verkleinert, bis er in höchstens vier Zeilen passt --
 * lesbar geht vor groß. Abgeschnitten wird nie: Ein halber Satz im Bild ist
 * schlimmer als ein kleinerer ganzer.
 */
export function layoutText(text, size, canvasWidth = CANVAS_W) {
    const clean = (text ?? '').trim();
    const scale = canvasWidth / CANVAS_W;
    const maxWidth = canvasWidth * TEXT_MAX_WIDTH;
    const start = (TEXT_SIZE_PX[size] ?? TEXT_SIZE_PX.M) * scale;
    const floor = MIN_FONT_PX * scale;
    if (!clean) {
        return { fontPx: start, lines: [], lineHeight: start * TEXT_LINE_HEIGHT, blockHeight: 0 };
    }
    // In feinen Schritten verkleinern, nicht in den vier groben Stufen: Der
    // Unterschied zwischen L und M ist ein Drittel, und dazwischen liegt fast
    // immer die Größe, bei der der Satz gerade eben passt.
    for (let fontPx = start; fontPx >= floor; fontPx *= 0.94) {
        const lines = wrapBalanced(clean, fontPx, maxWidth, MAX_LINES);
        if (!lines)
            continue;
        /*
         * Die Abwägung „groß in vielen Zeilen" gegen „etwas kleiner in
         * wenigeren": Ab drei Zeilen wird geprüft, ob ein bis zwei Stufen
         * kleiner eine Zeile spart. „Vier Symbole / aus einer / Tasse. Nicht /
         * vierzig." liest sich zerhackt; minimal kleiner wird daraus „Vier
         * Symbole / aus einer Tasse. / Nicht vierzig." -- drei volle Zeilen,
         * und der Größenunterschied ist mit bloßem Auge kaum zu sehen.
         */
        let chosen = { fontPx, lines };
        if (lines.length >= 3) {
            for (const f of [fontPx * 0.94, fontPx * 0.88]) {
                if (f < floor)
                    break;
                const alt = wrapBalanced(clean, f, maxWidth, MAX_LINES);
                if (alt && alt.length < chosen.lines.length) {
                    chosen = { fontPx: f, lines: alt };
                    break;
                }
            }
        }
        const lineHeight = Math.round(chosen.fontPx * TEXT_LINE_HEIGHT);
        return {
            fontPx: Math.round(chosen.fontPx),
            lines: chosen.lines,
            lineHeight,
            blockHeight: chosen.lines.length * lineHeight,
        };
    }
    // Selbst am unteren Anschlag zu lang: umbrechen, was geht, ohne Deckel.
    const lines = wrapGreedy(clean.split(/\s+/).filter(Boolean), floor, maxWidth);
    const lineHeight = Math.round(floor * TEXT_LINE_HEIGHT);
    return { fontPx: Math.round(floor), lines, lineHeight, blockHeight: lines.length * lineHeight };
}
/* ----------------------------------------------------------- Farbgebung */
/** Relative Helligkeit einer Hex-Farbe, 0 = schwarz, 1 = weiß. */
function luminance(hex) {
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const n = parseInt(full.slice(0, 6), 16);
    if (Number.isNaN(n))
        return 0;
    const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
        const s = v / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** Schwarz oder Weiß, je nachdem, was auf dieser Fläche lesbar ist. */
export function contrastInk(background) {
    return luminance(background) > 0.45 ? '#111111' : '#ffffff';
}
/**
 * Wie eine Textebene eingefärbt wird.
 *
 * Vorher hieß `highlight` zweierlei gleichzeitig: Der Text wurde in der
 * Akzentfarbe gemalt UND bekam einen dunklen Kasten. Lila Schrift auf
 * grauem Kasten auf dunklem Foto war das Ergebnis, dreifach kontrastarm.
 *
 * Jetzt trägt die Akzentfarbe den BALKEN, und die Schrift bekommt die Farbe,
 * die darauf lesbar ist. Das ist genau, was Instagram und TikTok tun, und es
 * repariert bestehende Posts ohne Migration: Ihre gespeicherte Akzentfarbe
 * wird einfach zur Balkenfarbe.
 */
export function textPaint(layer) {
    const color = layer.color || '#ffffff';
    if (layer.highlight)
        return { ink: contrastInk(color), bar: color };
    // Ohne Balken ist Weiß die einzige Farbe, die auf jedem Foto trägt.
    // Eine dunkle Wunschfarbe ohne Balken wäre auf einem Nachtbild unsichtbar.
    return { ink: luminance(color) > 0.5 ? color : '#ffffff', bar: null };
}
/**
 * Wo der Textblock senkrecht sitzt, in Pixeln, geklemmt in die sichere Zone.
 *
 * `y` ist der Mittelpunkt in Prozent, so wie ihn der Planer setzt und wie er
 * sich in der Vorschau ziehen lässt.
 */
export function blockTop(yPercent, blockHeight, canvasHeight = CANVAS_H) {
    const safeTop = canvasHeight * TEXT_SAFE_TOP;
    const safeBottom = canvasHeight * TEXT_SAFE_BOTTOM;
    const wanted = (yPercent / 100) * canvasHeight - blockHeight / 2;
    return Math.max(safeTop, Math.min(wanted, Math.max(safeTop, safeBottom - blockHeight)));
}
/**
 * Setzt mehrere Textebenen einer Szene übereinander, ohne dass sie sich
 * überlappen.
 *
 * Zwei Ebenen, deren Blöcke ineinanderlaufen, waren bisher der Normalfall:
 * Jede wurde für sich auf ihr y zentriert, und dass die eine fünf Zeilen
 * hoch ist, wusste die andere nicht. Hier laufen sie durch dieselbe
 * Rechnung, in Lesereihenfolge, mit einem festen Abstand dazwischen.
 */
export function layoutScene(layers, canvasWidth = CANVAS_W, canvasHeight = CANVAS_H) {
    const scale = canvasWidth / CANVAS_W;
    const gap = Math.round(26 * scale);
    const placed = layers
        .filter((l) => (l.text ?? '').trim())
        .sort((a, b) => (a.y ?? 30) - (b.y ?? 30))
        .map((layer) => {
        const layout = layoutText(layer.text, layer.size, canvasWidth);
        return { layer, layout, paint: textPaint(layer), top: blockTop(layer.y ?? 34, layout.blockHeight, canvasHeight) };
    });
    // Von oben nach unten auseinanderschieben, danach den Überstand unten
    // gemeinsam nach oben holen, damit nichts hinter der Plattformleiste landet.
    for (let i = 1; i < placed.length; i++) {
        const prev = placed[i - 1];
        const min = prev.top + prev.layout.blockHeight + gap;
        if (placed[i].top < min)
            placed[i].top = min;
    }
    const last = placed[placed.length - 1];
    const safeBottom = canvasHeight * TEXT_SAFE_BOTTOM;
    if (last && last.top + last.layout.blockHeight > safeBottom) {
        const shift = last.top + last.layout.blockHeight - safeBottom;
        const safeTop = canvasHeight * TEXT_SAFE_TOP;
        const room = placed[0].top - safeTop;
        const move = Math.min(shift, Math.max(0, room));
        for (const p of placed)
            p.top -= move;
    }
    return placed;
}
//# sourceMappingURL=textLayout.js.map